import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DentalTreatRule } from './dental-treat-rule-data-from-eprohis';
import { DentalHolidayCalendarCacheService } from 'src/app/services/dental-holiday-calendar-cache.service';
import { getDentalRequiredCardViolations } from './dental-required-card-rules';

export const DENTAL_DOUBLE_ADJACENT_STD_CODES: ReadonlyArray<string> = [
  '89014C',
  '89015C',
  '89114C',
  '89115C',
  '89214C',
  '89215C',
  '89314C',
  '89315C'
];

export function isDentalDoubleAdjacentStdCode(code: string): boolean {
  const normalizedCode = (code || '').trim().toUpperCase();
  if (!normalizedCode) {
    return false;
  }

  const standardCode = normalizedCode.endsWith('C')
    ? normalizedCode
    : normalizedCode + 'C';

  return DENTAL_DOUBLE_ADJACENT_STD_CODES.indexOf(standardCode) >= 0;
}

/** 規則嚴重性等級（由高到低）：extracted > forbidden > warning > info */
export type RuleSeverity = 'extracted' | 'forbidden' | 'warning' | 'info';

export type DentalChartHistoryCategory = 'od' | 'endo';

/** 衝突來源（歷史紀錄或當前 grid） */
export interface RuleConflict {
  stdCode: string;
  toothPos?: string;
  /** 民國年格式（僅 history 有） */
  regDate?: string;
  source: 'history' | 'grid';
}

/** 帶嚴重性的規則訊息 */
export interface RuleMessage {
  text: string;
  severity: RuleSeverity;
  /** 病歷規則面板需要跨列彙總時，覆寫訊息列所顯示的牙位。 */
  toothPosOverride?: string;
  /** 牙位 helper 是否顯示「X」（僅 extracted + 拔牙/齒切除術） */
  showX?: boolean;
  /** 造成違規的衝突來源（病歷檢查面板用） */
  conflicts?: RuleConflict[];
}

/** 規則檢核所需的上下文 */
export interface RuleContext {
  /** 本次看診日 */
  regDate: Date;
  /** 當前 grid 中已存在的所有 StdCode（含本筆） */
  existingStdCodes: string[];
  /** 當前 grid 中每列的 StdCode + ToothPos + Id + 原始列號（供 isfdi/isquadrant 比對 & HRxId 過濾） */
  existingGridEntries?: { StdCode: string; ToothPos: string; Id?: number; RowIndex?: number }[];
  /** 院內、IC 與雲端歷史處置紀錄 */
  clinicHistories: {
    StdCode: string;
    RegDate: string;
    ToothPos: string;
    DxCodes?: string[];
    HistorySource?: 'internal' | 'ic' | 'cloud';
    IsLocal?: boolean;
  }[];
  /** 就醫類別（如 '01','AB' 等；對應 Register.Nhictp） */
  registerClass: string;
  /** 同療類別（對應 Register.SameTreat，例如 '5'=牙科同療、'6'=牙統療程） */
  sameTreat?: string;
  /** Current SameTreat 5/6 course tooth positions. AB rules use this to avoid mixing other courses. */
  sameTreatmentToothPositions?: string[];
  /** Current case type for dental cross-row rules. */
  caseType?: string;
  /** True when the current dental grid contains 91021C/91022C/91023C. */
  hasPerioIntegratedCare?: boolean;
  /** Original/suggested case type before perio integrated care forces case 19. */
  perioIntegratedCareCase?: string;
  /** 當前檢核列的牙位（供 isfdi/isquadrant 做同牙位/同象限過濾） */
  currentToothPos?: string;
  /** 當前檢核列在 grid 的行索引（disable2 排除自己用） */
  currentRowIndex?: number;
}

/** 單筆規則檢核結果 */
export interface RuleResult {
  passed: boolean;
  errorMessages: string[];
  /** 帶嚴重性的訊息陣列 */
  messages: RuleMessage[];
  /** 最嚴重的等級 */
  maxSeverity: RuleSeverity | null;
}

/** 病歷檢查面板用：一列 grid 的違規彙整 */
export interface RuleViolationEntry {
  rowIndex: number;
  stdCode: string;
  toothPos: string;
  messages: RuleMessage[];
}

const SEVERITY_ORDER: Record<RuleSeverity, number> = { extracted: 0, forbidden: 1, warning: 2, info: 3 };

export function maxSeverityOf(a: RuleSeverity | null, b: RuleSeverity): RuleSeverity {
  if (!a) return b;
  return SEVERITY_ORDER[a] <= SEVERITY_ORDER[b] ? a : b;
}

/** 部位/象限 → 個別齒位映射 */
const FULL_MOUTH_TEETH = [
  '11','12','13','14','15','16','17','18','19',
  '21','22','23','24','25','26','27','28','29',
  '31','32','33','34','35','36','37','38','39',
  '41','42','43','44','45','46','47','48','49',
  '51','52','53','54','55',
  '61','62','63','64','65',
  '71','72','73','74','75',
  '81','82','83','84','85',
];

const REGION_TO_TEETH: Record<string, string[]> = {
  UR: ['11','12','13','14','15','16','17','18','19','51','52','53','54','55'],
  UA: ['11','12','13','19','21','22','23','29','51','52','53','61','62','63'],
  UL: ['21','22','23','24','25','26','27','28','29','61','62','63','64','65'],
  UB: ['14','15','16','17','18','19','24','25','26','27','28','29','54','55','64','65'],
  LL: ['31','32','33','34','35','36','37','38','39','71','72','73','74','75'],
  LA: ['31','32','33','39','41','42','43','49','71','72','73','81','82','83'],
  LR: ['41','42','43','44','45','46','47','48','49','81','82','83','84','85'],
  LB: ['34','35','36','37','38','39','44','45','46','47','48','49','74','75','84','85'],
};

const PERIO_INTEGRATED_CARE_CODES = new Set<string>(['91021C', '91022C', '91023C']);
const DENTAL_CHART_MANUAL_MARKER_CODE = '牙圖手動標記';
const DENTAL_CHART_UNAVAILABLE_TOOTH_MESSAGE = '該牙位不存在，不可再申報牙齒相關處置';
const PERIO_INTEGRATED_CARE_ALLOWED_CODES = new Set<string>([
  '91021C', '91022C', '91023C', '96001C', '34001C', '34002C'
]);
const TREAT_91013C = '91013C';
const TREAT_91013C_CONCURRENT_CONFLICT_CODES = new Set<string>(['91011C', '91012C']);
const TREAT_91014C = '91014C';
const TREAT_91014C_SCALING_COMPANION_CODES = new Set<string>(['91003C', '91004C', '91005C']);
const TREAT_91014C_SUPPORTIVE_COMPANION_CODE = '91020C';

@Injectable({ providedIn: 'root' })
export class DentalTreatRuleService {

  private rulesByNhitrcode: Map<string, DentalTreatRule[]>;
  private rulesByTrcode: Map<string, DentalTreatRule[]>;
  private allRules: DentalTreatRule[] = [];
  private initialized = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private dentalHolidayCalendar: DentalHolidayCalendarCacheService) {
    this.rulesByNhitrcode = new Map();
    this.rulesByTrcode = new Map();
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (!this.loadingPromise) {
      this.loadingPromise = this.loadRules();
    }

    await this.loadingPromise;
  }

  private async loadRules(): Promise<void> {
    try {
      const rules = await this.http.get<DentalTreatRule[]>('/assets/data/dental-treat-rules.json').toPromise();
      this.allRules = rules ?? [];
      this.rebuildIndexes();
      this.initialized = true;
    } catch (err) {
      this.initialized = false;
      this.allRules = [];
      this.rebuildIndexes();
      throw err;
    }
  }

  private rebuildIndexes(): void {
    this.rulesByNhitrcode = new Map();
    this.rulesByTrcode = new Map();

    for (const rule of this.allRules) {
      if (rule.nhitrcode) {
        const bucket = this.rulesByNhitrcode.get(rule.nhitrcode);
        if (bucket) bucket.push(rule);
        else this.rulesByNhitrcode.set(rule.nhitrcode, [rule]);
      }

      if (rule.trcode) {
        const bucket2 = this.rulesByTrcode.get(rule.trcode);
        if (bucket2) bucket2.push(rule);
        else this.rulesByTrcode.set(rule.trcode, [rule]);
      }
    }
  }

  // ─── 查詢 API ───

  get ruleCount(): number { return this.allRules.length; }

  getRulesByNhitrcode(nhitrcode: string): DentalTreatRule[] {
    if (!this.initialized && !this.loadingPromise) {
      this.loadingPromise = this.loadRules();
    }
    return this.rulesByNhitrcode.get(nhitrcode) || [];
  }

  getRulesByTrcode(trcode: string): DentalTreatRule[] {
    if (!this.initialized && !this.loadingPromise) {
      this.loadingPromise = this.loadRules();
    }
    return this.rulesByTrcode.get(trcode) || [];
  }

  /** 查詢當日有效的單次申報數量限制；標準碼及規則處置碼皆可查詢。 */
  isUnitQuantityFixedToOne(code: string, regDate: Date): boolean {
    const normalizedCode = (code || '').trim().toUpperCase();
    if (!this.initialized || !normalizedCode) return false;
    const date = new Date(regDate);
    if (isNaN(date.getTime())) return false;
    date.setHours(0, 0, 0, 0);
    const rules = (this.rulesByNhitrcode.get(normalizedCode) || [])
      .concat(this.rulesByTrcode.get(normalizedCode) || []);
    return rules.some(rule => rule.unit === 1 && this.checkDateRange(rule, date));
  }

  getRulesBatchByNhitrcode(nhitrcodes: string[]): Map<string, DentalTreatRule[]> {
    if (!this.initialized && !this.loadingPromise) {
      this.loadingPromise = this.loadRules();
    }

    const result = new Map<string, DentalTreatRule[]>();
    for (const code of nhitrcodes) {
      const rules = this.rulesByNhitrcode.get(code);
      if (rules && rules.length > 0) {
        result.set(code, rules);
      }
    }
    return result;
  }

  getAllNhitrcodes(): string[] {
    if (!this.initialized && !this.loadingPromise) {
      this.loadingPromise = this.loadRules();
    }
    return Array.from(this.rulesByNhitrcode.keys());
  }

  // ─── 規則檢核 API ───

  /** 根據 errMsg 內容判定嚴重性等級 */
  classifySeverity(msg: string): RuleSeverity {
    if (/已執行過拔牙|已執行過拔牙或齒切除術/.test(msg)) return 'extracted';
    if (/不可|不得|請改掛|已內含|僅能|需間隔|需相隔|只能申報一次/.test(msg)) return 'forbidden';
    if (/不應|[應須需]/.test(msg)) return 'warning';
    return 'info';
  }

  /**
   * OD 特殊規則：依乳/恆齒 × 距上次天數 → 覆寫 severity / msg
   * 乳齒 <365d → forbidden 「乳牙1年內不可重複執行OD處置」
   * 恆齒 <545d → forbidden 「恆牙1年半內不可重複執行OD處置」
   * 乳齒 365~545d → warning 「乳牙1年半內將計算OD重補率」
   * 恆齒 545~730d → warning 「恆牙2年內將計算OD重補率」
   */
  private classifyOD(
    toothPos: string | null | undefined,
    latestDate: Date | null,
    regDate: Date
  ): RuleMessage[] {
    if (!toothPos || !latestDate) return [];
    const positions = this.splitCodes(toothPos);
    if (positions.length === 0) return [];

    const results: RuleMessage[] = [];

    // 每個牙位獨立判斷乳/恆齒，產生專屬訊息
    for (const pos of positions) {
      const num = parseInt(pos, 10);
      if (isNaN(num)) continue;
      const tens = Math.floor(num / 10);
      const isDeciduous = DentalTreatRuleService.DECIDUOUS_TENS.has(tens);
      const severity = this.getODHistorySeverity(pos, latestDate, regDate);

      if (isDeciduous) {
        if (severity === 'forbidden')
          results.push({ text: `牙位${pos}: 乳牙1年內不可重複執行OD處置`, severity: 'forbidden' });
        else if (severity === 'warning')
          results.push({ text: `牙位${pos}: 乳牙1年半內將計算OD重補率`, severity: 'warning' });
      } else {
        if (severity === 'forbidden')
          results.push({ text: `牙位${pos}: 恆牙1年半內不可重複執行OD處置`, severity: 'forbidden' });
        else if (severity === 'warning')
          results.push({ text: `牙位${pos}: 恆牙2年內將計算OD重補率`, severity: 'warning' });
      }
    }
    return results;
  }

  /** 病歷規則面板的既有 OD 訊息嚴重度。牙歷圖改由逐碼規則解析器計算。 */
  getODHistorySeverity(toothPos: string, latestDate: Date | null, regDate: Date): RuleSeverity | null {
    if (!toothPos || !latestDate) return null;
    const num = parseInt(toothPos, 10);
    if (isNaN(num)) return null;

    const diffDays = Math.floor((regDate.getTime() - latestDate.getTime()) / 86400000);
    const tens = Math.floor(num / 10);
    const isDeciduous = DentalTreatRuleService.DECIDUOUS_TENS.has(tens);
    if (isDeciduous) {
      if (diffDays < 365) return 'forbidden';
      if (diffDays < 545) return 'warning';
      return null;
    }

    if (diffDays < 545) return 'forbidden';
    if (diffDays < 730) return 'warning';
    return null;
  }

  /**
   * 依牙科規則資料計算牙歷圖歷史標記嚴重度。
   *
   * DecreeRx 群組只決定處置屬於 OD 或 ENDO；實際日期門檻則從同一份
   * dental-treat-rules.json 的 disable1 + period_k/period_a 關聯解析，避免
   * 牙歷圖另維護一套固定天數。沒有日期門檻的有效處置保留為資訊標記。
   */
  getDentalChartHistorySeverity(
    stdCode: string,
    category: DentalChartHistoryCategory,
    categoryCodes: Set<string>,
    historyDate: Date | null,
    regDate: Date
  ): RuleSeverity | null {
    const normalizedCode = this.normalizeDentalCode(stdCode);
    if (!normalizedCode || !categoryCodes.has(normalizedCode) || !historyDate) return null;

    const currentDate = this.startOfDay(regDate);
    const treatmentDate = this.startOfDay(historyDate);
    if (isNaN(currentDate.getTime()) || isNaN(treatmentDate.getTime()) || treatmentDate > currentDate) {
      return null;
    }

    const periods = this.getDentalChartHistoryPeriods(normalizedCode, category, categoryCodes, currentDate);
    if (periods.length === 0) return 'info';

    // periods 由短至長排序；第一個門檻內為不可，後續門檻內為警示。
    for (let index = 0; index < periods.length; index++) {
      if (treatmentDate >= periods[index]) {
        return index === 0 ? 'forbidden' : 'warning';
      }
    }
    return null;
  }

  /** 將牙科處置碼統一為帶 C 尾碼的比較格式。 */
  normalizeDentalCode(code: string): string {
    const normalized = String(code || '').trim().toUpperCase();
    return /^\d{5}$/.test(normalized) ? `${normalized}C` : normalized;
  }

  /** 依規則資料中的 FDI 限制確認歷史處置是否可標在該牙位。 */
  isDentalChartToothPositionAllowed(stdCode: string, toothPos: string, regDate: Date): boolean {
    const code = this.normalizeDentalCode(stdCode);
    const rules = this.getRulesByNhitrcode(code)
      .filter(rule => !!rule.fdi && this.checkDateRange(rule, regDate));
    if (rules.length === 0) return true;

    const allowedPositions = new Set<string>();
    rules.forEach(rule => this.splitCodes(rule.fdi || '').forEach(position => allowedPositions.add(position)));
    return this.splitCodes(toothPos).some(position => allowedPositions.has(position));
  }

  private getDentalChartHistoryPeriods(
    historyCode: string,
    category: DentalChartHistoryCategory,
    categoryCodes: Set<string>,
    regDate: Date
  ): Date[] {
    const cutoffs = new Map<number, Date>();
    for (const rule of this.allRules) {
      const targetCode = this.normalizeDentalCode(rule.nhitrcode);
      if (!targetCode || !categoryCodes.has(targetCode) || !this.checkDateRange(rule, regDate)) continue;

      const periodKind = String(rule.period_k || '').trim().toUpperCase();
      const periodAmount = Number(rule.period_a || 0);
      if (!rule.disable1 || rule.times <= 0 || periodAmount <= 0 || !['D', 'M', 'Y'].includes(periodKind)) continue;

      const disabledCodes = this.splitCodes(rule.disable1).map(code => this.normalizeDentalCode(code));
      if (!disabledCodes.includes(historyCode)) continue;

      // categoryCodes 已由 API 群組傳入；此查詢保留 category 參數，讓 OD/ENDO
      // 的規則解析路徑明確分開，避免未來跨類別規則誤套用。
      if (category !== 'od' && category !== 'endo') continue;
      const cutoff = this.calcCutoffDate(regDate, periodKind, periodAmount);
      if (cutoff) cutoffs.set(cutoff.getTime(), this.startOfDay(cutoff));
    }

    return Array.from(cutoffs.values()).sort((a, b) => b.getTime() - a.getTime());
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  /** 檢核單一 nhitrcode 的所有規則，全部通過才算 passed */
  private getPerioIntegratedCareReverseMessage(nhitrcode: string, ctx: RuleContext): RuleMessage | null {
    // 卡別不符須先退掛；此時不先顯示案類建議，其他處置規則仍照常檢核。
    if (getDentalRequiredCardViolations([...(ctx?.existingStdCodes || []), nhitrcode], ctx?.registerClass).length > 0) {
      return null;
    }
    const code = (nhitrcode || '').trim().toUpperCase();
    if (!ctx?.hasPerioIntegratedCare) return null;
    if (!ctx.perioIntegratedCareCase) return null;
    if (PERIO_INTEGRATED_CARE_ALLOWED_CODES.has(code)) return null;

    const conflictStdCodes: string[] = [];
    const conflictToothPositions: string[] = [];
    const perioEntries = (ctx.existingGridEntries || [])
      .filter(e => PERIO_INTEGRATED_CARE_CODES.has((e.StdCode || '').trim().toUpperCase()));
    for (const entry of perioEntries) {
      const stdCode = (entry.StdCode || '').trim().toUpperCase();
      if (stdCode && !conflictStdCodes.includes(stdCode)) {
        conflictStdCodes.push(stdCode);
      }
      const toothPos = (entry.ToothPos || '').trim();
      if (toothPos && !conflictToothPositions.includes(toothPos)) {
        conflictToothPositions.push(toothPos);
      }
    }
    if (conflictStdCodes.length === 0) {
      for (const stdCode of (ctx.existingStdCodes || []).map(c => (c || '').trim().toUpperCase())) {
        if (PERIO_INTEGRATED_CARE_CODES.has(stdCode) && !conflictStdCodes.includes(stdCode)) {
          conflictStdCodes.push(stdCode);
        }
      }
    }
    if (conflictStdCodes.length === 0) return null;

    const regDate = new Date(ctx.regDate);
    const rocRegDate = isNaN(regDate.getTime())
      ? undefined
      : `${regDate.getFullYear() - 1911}/${String(regDate.getMonth() + 1).padStart(2, '0')}/${String(regDate.getDate()).padStart(2, '0')}`;

    return {
      text: `本筆掛號含牙統處置必為19案類，建議將${code}另外掛號並選擇${ctx.perioIntegratedCareCase}案類`,
      severity: 'info',
      conflicts: [{
        stdCode: conflictStdCodes.join(' '),
        toothPos: conflictToothPositions.join(' '),
        regDate: rocRegDate,
        source: 'grid',
      }]
    };
  }

  private getTreat91014CIntervalMessage(nhitrcode: string, ctx: RuleContext): RuleMessage | null {
    const code = (nhitrcode || '').trim().toUpperCase();
    if (code !== TREAT_91014C) return null;

    const existingCodes = new Set((ctx.existingStdCodes || []).map(c => (c || '').trim().toUpperCase()).filter(c => c));
    const scalingCompanions = Array.from(TREAT_91014C_SCALING_COMPANION_CODES).filter(c => existingCodes.has(c));
    const hasSupportiveCompanion = existingCodes.has(TREAT_91014C_SUPPORTIVE_COMPANION_CODE);
    const thresholdDays = scalingCompanions.length > 0 ? 360 : (hasSupportiveCompanion ? 180 : null);
    if (thresholdDays == null) return null;

    const regDate = new Date(ctx.regDate);
    if (isNaN(regDate.getTime())) return null;

    let latestHistory: { StdCode: string; RegDate: string; ToothPos: string; DxCodes?: string[] } | null = null;
    let latestHistoryDate: Date | null = null;
    for (const history of ctx.clinicHistories || []) {
      if ((history.StdCode || '').trim().toUpperCase() !== TREAT_91014C) continue;
      const historyDate = this.parseRocDate(history.RegDate);
      if (!historyDate || historyDate > regDate) continue;
      if (!latestHistoryDate || historyDate > latestHistoryDate) {
        latestHistory = history;
        latestHistoryDate = historyDate;
      }
    }
    if (!latestHistory || !latestHistoryDate) return null;

    const latestDate = new Date(latestHistoryDate);
    const currentDate = new Date(regDate);
    latestDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((currentDate.getTime() - latestDate.getTime()) / 86400000);
    if (diffDays >= thresholdDays) return null;

    const companionText = scalingCompanions.length > 0
      ? scalingCompanions.join('、')
      : TREAT_91014C_SUPPORTIVE_COMPANION_CODE;
    return {
      text: `併同${companionText}申報91014C時，距離前一次91014C未滿${thresholdDays}天，不得申報`,
      severity: 'forbidden',
      conflicts: [{
        stdCode: latestHistory.StdCode,
        toothPos: latestHistory.ToothPos || undefined,
        regDate: latestHistory.RegDate,
        source: 'history',
      }]
    };
  }

  validateAllRules(nhitrcode: string, ctx: RuleContext): RuleResult {
    const dentalHolidayMessage = this.dentalHolidayCalendar.getRuleMessage(nhitrcode, ctx.regDate);
    const unavailableToothMessage = this.getDentalChartUnavailableToothMessage(ctx);
    if (unavailableToothMessage) {
      return this.createRuleResult(
        dentalHolidayMessage ? [dentalHolidayMessage, unavailableToothMessage] : [unavailableToothMessage]);
    }

    const rules = this.getRulesByNhitrcode(nhitrcode);
    const allMessages: RuleMessage[] = [];
    if (dentalHolidayMessage) {
      allMessages.push(dentalHolidayMessage);
    }
    const reversePerioMessage = this.getPerioIntegratedCareReverseMessage(nhitrcode, ctx);
    if (reversePerioMessage) {
      allMessages.push(reversePerioMessage);
    }
    const treat91014CIntervalMessage = this.getTreat91014CIntervalMessage(nhitrcode, ctx);
    if (treat91014CIntervalMessage) {
      allMessages.push(treat91014CIntervalMessage);
    }

    for (const rule of rules) {
      const r = this.validateRule(rule, ctx);
      if (!r.passed) {
        allMessages.push(...r.messages);
      }
    }

    // Dedup：若同時存在「該牙位已執行過拔牙處置」和「該牙位已執行過拔牙或齒切除術，不可再申報此處置」→ 移除前者
    const hasExtLong = allMessages.some(m => m.text.includes('已執行過拔牙或齒切除術'));
    const deduped = hasExtLong
      ? allMessages.filter(m => !(m.text === '該牙位已執行過拔牙處置'))
      : allMessages;

    const adjustedMessages = this.adjustExternalHistorySeverities(deduped, ctx);
    let maxSev: RuleSeverity | null = null;
    for (const m of adjustedMessages) maxSev = maxSeverityOf(maxSev, m.severity);

    return {
      passed: adjustedMessages.length === 0,
      errorMessages: adjustedMessages.map(m => m.text),
      messages: adjustedMessages,
      maxSeverity: maxSev,
    };
  }

  shouldHideDentalOrderHelper(code: string, regDate: Date): boolean {
    return this.dentalHolidayCalendar.shouldHideFromDentalOrderHelper(code, regDate);
  }

  private createRuleResult(messages: RuleMessage[]): RuleResult {
    let maxSeverity: RuleSeverity | null = null;
    for (const message of messages) {
      maxSeverity = maxSeverityOf(maxSeverity, message.severity);
    }

    return {
      passed: messages.length === 0,
      errorMessages: messages.map(message => message.text),
      messages,
      maxSeverity,
    };
  }

  private getDentalChartUnavailableToothMessage(ctx: RuleContext, toothPos = ctx.currentToothPos): RuleMessage | null {
    if (!toothPos) return null;
    const selectedTeeth = new Set(toothPos.split(',')
      .map(position => position.trim())
      .filter(position => /^\d{2}$/.test(position)));
    if (selectedTeeth.size === 0) return null;
    const markerHistories = (ctx.clinicHistories || []).filter(history =>
      (history.StdCode || '').trim() === DENTAL_CHART_MANUAL_MARKER_CODE &&
      (history.ToothPos || '').split(',').some(position => selectedTeeth.has(position.trim())));
    if (markerHistories.length === 0) return null;

    return {
      text: DENTAL_CHART_UNAVAILABLE_TOOTH_MESSAGE,
      severity: 'extracted',
      showX: true,
      conflicts: markerHistories.map(history => ({
        stdCode: DENTAL_CHART_MANUAL_MARKER_CODE,
        toothPos: history.ToothPos,
        regDate: history.RegDate,
        source: 'history',
      })),
    };
  }

  private adjustExternalHistorySeverities(
    messages: RuleMessage[],
    ctx: Pick<RuleContext, 'clinicHistories'>
  ): RuleMessage[] {
    return messages.map(message => {
      const conflicts = message.conflicts || [];
      const historyConflicts = conflicts.filter(conflict => conflict.source === 'history');
      if (historyConflicts.length === 0 || conflicts.some(conflict => conflict.source === 'grid')) {
        return message;
      }

      const matchedHistories = historyConflicts.map(conflict =>
        (ctx.clinicHistories || []).filter(history =>
          history.StdCode === conflict.stdCode &&
          (!conflict.regDate || history.RegDate === conflict.regDate) &&
          (!conflict.toothPos || history.ToothPos === conflict.toothPos)
        ));
      if (matchedHistories.some(histories => histories.length === 0) ||
        matchedHistories.some(histories => histories.some(history => this.isLocalHistory(history)))) {
        return message;
      }

      const preserveExternalExtraction = message.severity === 'extracted' &&
        historyConflicts.some(conflict => /^92/.test(conflict.stdCode || ''));
      if (preserveExternalExtraction) return message;

      return { ...message, severity: this.downgradeSeverity(message.severity) };
    });
  }

  private downgradeSeverity(severity: RuleSeverity): RuleSeverity {
    if (severity === 'extracted') return 'forbidden';
    if (severity === 'forbidden') return 'warning';
    return 'info';
  }

  private isLocalHistory(history: RuleContext['clinicHistories'][number]): boolean {
    if (history.HistorySource === 'internal') return true;
    if (history.IsLocal === false) return false;
    if (history.IsLocal === true) return true;
    // IC／雲端未提供院所判定時，不將來源誤當成院內。
    if (history.HistorySource === 'ic' || history.HistorySource === 'cloud') return false;
    return true;
  }

  /** 批次檢核多個 nhitrcode */
  validateBatch(nhitrcodes: string[], ctx: RuleContext): Map<string, RuleResult> {
    const result = new Map<string, RuleResult>();
    for (const code of nhitrcodes) {
      result.set(code, this.validateAllRules(code, ctx));
    }
    return result;
  }

  private static readonly PASS_RESULT: RuleResult = { passed: true, errorMessages: [], messages: [], maxSeverity: null };

  /** 乳齒齒號 tens digit */
  private static readonly DECIDUOUS_TENS = new Set([5, 6, 7, 8]);
  /** 只算年月差的代碼 */
  private static readonly CALENDAR_MONTH_DIFFERENCE_CODES = new Set([
    '81', '87', '88', '89', '91001C', '92161B',
  ]);

  /** 檢核單條規則 */
  validateRule(rule: DentalTreatRule, ctx: RuleContext): RuleResult {
    const messages: RuleMessage[] = [];
    const regDate = new Date(ctx.regDate);
    // 產生民國年格式供 grid 衝突的日期欄使用
    const rocRegDate = `${regDate.getFullYear() - 1911}/${String(regDate.getMonth() + 1).padStart(2, '0')}/${String(regDate.getDate()).padStart(2, '0')}`;

    // 1. start_date / end_date 有效期間
    if (!this.checkDateRange(rule, regDate)) {
      return DentalTreatRuleService.PASS_RESULT;
    }

    // 1.5 fdi 欄位
    if (rule.fdi && ctx.currentToothPos) {
      const allowedPositions = this.splitCodes(rule.fdi);
      const currentPositions = this.splitCodes(ctx.currentToothPos);
      const hasAllowedPosition = rule.checkAB === 1 && !!rule.ABparent
        ? this.hasABPositionOverlap(currentPositions, allowedPositions)
        : currentPositions.some(p => allowedPositions.includes(p));
      if (!hasAllowedPosition) {
        return DentalTreatRuleService.PASS_RESULT;
      }
    }

    // 2. checkAB / ABparent（含 period_k + period_a 日期過濾）
    // 分三種情境：
    //   A. registerClass === 'AB'：當前掛號為AB療程
    //      - 已有 ABparent 紀錄：currentToothPos 必須落在「療程合理牙位集合」內，否則 forbidden
    //      - 無 ABparent 紀錄：無從檢核（pass）
    //   B. registerClass !== 'AB'：
    //      - currentToothPos 在 ABparent 紀錄牙位內 → forbidden（要求改掛AB）
    //      - 否則 row-level 不違規（helper 反向標記統一用 info）
    if (rule.checkAB === 1 && rule.ABparent) {
      const abInfo = this.collectABParentInfo(rule, ctx, regDate);
      const isAB = ctx.registerClass === 'AB';

      if (rule.isfdi === 1) {
        if (ctx.currentToothPos) {
          const currentPositions = this.splitCodes(ctx.currentToothPos).map(p => p.toUpperCase());
          const allCurrentInValid = currentPositions.length > 0
            && currentPositions.every(p => this.hasABPositionOverlap([p], abInfo.validPositions));

          if (isAB) {
            // 已是AB療程：若有療程紀錄但牙位不在合理範圍內 → forbidden
            if (abInfo.validPositions.size > 0 && !allCurrentInValid) {
              const validList = this.formatValidPositions(abInfo.validPositions);
              const msg = `本次療程開始日${abInfo.startDateStr}僅能處理${validList}，如需處理其他部位請另掛一筆或變更本次掛號療程`;
              const conflicts: RuleConflict[] = [];
              for (const h of abInfo.parentHistories) {
                conflicts.push({ stdCode: h.StdCode, toothPos: h.ToothPos || undefined, regDate: h.RegDate, source: 'history' });
              }
              for (const e of abInfo.parentGridEntries) {
                conflicts.push({ stdCode: e.StdCode, toothPos: e.ToothPos || undefined, regDate: rocRegDate, source: 'grid' });
              }
              messages.push({ text: msg, severity: 'forbidden', conflicts: conflicts.length > 0 ? conflicts : undefined });
            }
          } else {
            // 非AB療程：牙位若在 ABparent 紀錄牙位內 → forbidden
            // 此處只看歷史（grid 內表示醫師當次正在開立母處置，視為合規）
            const anyInHistOnly = this.hasABPositionOverlap(currentPositions, abInfo.histPositions);
            const anyInGrid = this.hasABPositionOverlap(currentPositions, abInfo.gridPositions);
            if (anyInHistOnly && !anyInGrid) {
              const msg = rule.errorMsg || '該牙位有療程開始日，請改掛AB療程！';
              const abConflicts: RuleConflict[] = [];
              for (const h of abInfo.parentHistories) {
                const hp = h.ToothPos ? this.splitCodes(h.ToothPos) : [];
                if (this.hasABPositionOverlap(currentPositions, hp)) {
                  abConflicts.push({ stdCode: h.StdCode, toothPos: h.ToothPos || undefined, regDate: h.RegDate, source: 'history' });
                }
              }
              messages.push({ text: msg, severity: this.classifySeverity(msg), conflicts: abConflicts.length > 0 ? abConflicts : undefined });
            }
            // 不在 ABparent 紀錄內 → helper 才會根據 sameTreat 標 warning/info
          }
        }
      } else {
        // isfdi=0：無牙位概念，照原邏輯
        const foundInHistory = abInfo.parentHistories.length > 0;
        const foundInGrid = abInfo.parentGridEntries.length > 0;
        if (!foundInGrid && foundInHistory && !isAB) {
          const msg = rule.errorMsg || '該牙位有療程開始日，請改掛AB療程！';
          const abConflicts: RuleConflict[] = abInfo.parentHistories.map(h => ({
            stdCode: h.StdCode, toothPos: h.ToothPos || undefined, regDate: h.RegDate, source: 'history' as const,
          }));
          messages.push({ text: msg, severity: this.classifySeverity(msg), conflicts: abConflicts.length > 0 ? abConflicts : undefined });
        }
      }
    }

    // 3. intrcode（isindep=1）
    if (rule.isindep === 1 && rule.intrcode) {
      const required = this.splitCodes(rule.intrcode);
      if (!required.some(c => ctx.existingStdCodes.includes(c))) {
        const msg = rule.errMsg_indep || '需與主處置合併申報';
        messages.push({ text: msg, severity: this.classifySeverity(msg) });
      } else if (rule.nhitrcode === TREAT_91013C && ctx.currentToothPos) {
        // 91013C 可合併多列主處置，但每顆申報牙位都必須被主處置涵蓋。
        const coveredTeeth = new Set<string>();
        this.getOtherGridEntries(ctx)
          .filter(entry => required.includes(entry.StdCode))
          .forEach(entry => this.expandToTeeth(entry.ToothPos).forEach(tooth => coveredTeeth.add(tooth)));
        const baseMsg = rule.errMsg_indep || '需與主處置合併申報';
        this.expandToTeeth(ctx.currentToothPos).forEach(tooth => {
          if (coveredTeeth.has(tooth)) return;
          messages.push({
            text: `${baseMsg}（未涵蓋牙位：${tooth}）`,
            severity: 'forbidden',
            toothPosOverride: tooth,
          });
        });
      }
    }

    // 4. intrcode2（isindep2=1）
    if (rule.isindep2 === 1 && rule.intrcode2) {
      const allowed = this.splitCodes(rule.intrcode2);
      if (ctx.existingStdCodes.some(c => c !== rule.nhitrcode && !allowed.includes(c))) {
        const msg = rule.errMsg_indep2 || '不可與非指定處置同時申報';
        messages.push({ text: msg, severity: this.classifySeverity(msg) });
      }
    }

    // 5. disable2
    if (rule.disable2) {
      const disableCodes = this.splitCodes(rule.disable2);
      const otherEntries = this.getOtherGridEntries(ctx);
      // 91013C 與 91011C/91012C 整筆病歷互斥，不受牙位是否重疊影響。
      const is91013CConcurrentConflict = (code: string) =>
        (rule.nhitrcode === TREAT_91013C && TREAT_91013C_CONCURRENT_CONFLICT_CODES.has(code))
        || (TREAT_91013C_CONCURRENT_CONFLICT_CODES.has(rule.nhitrcode) && code === TREAT_91013C);
      const concurrentConflicts = otherEntries.filter(entry =>
        disableCodes.includes(entry.StdCode) && is91013CConcurrentConflict(entry.StdCode));
      if (concurrentConflicts.length > 0) {
        messages.push({
          text: '91013C不可與91011C或91012C同時申報',
          severity: 'forbidden',
          conflicts: concurrentConflicts.map(entry => ({
            stdCode: entry.StdCode,
            toothPos: entry.ToothPos || undefined,
            regDate: rocRegDate,
            source: 'grid' as const,
          })),
        });
      }
      if (rule.isfdi === 1) {
        if (ctx.currentToothPos && otherEntries.length > 0) {
          const currentPositions = this.splitCodes(ctx.currentToothPos);
          const conflicts = otherEntries.filter(e => {
            if (!disableCodes.includes(e.StdCode) || is91013CConcurrentConflict(e.StdCode)) return false;
            if (!e.ToothPos) return false;
            const ep = this.splitCodes(e.ToothPos);
            return rule.isquadrant === 1
              ? this.hasQuadrantOverlap(currentPositions, ep)
              : this.hasPositionOverlap(currentPositions, ep);
          });
          if (conflicts.length > 0) {
            const msg = rule.errMsg_trcode || (rule.isquadrant === 1
              ? '當日同象限同處置不可重複申報' : '當日同牙位同處置不可重複申報');
            const d2Conflicts: RuleConflict[] = conflicts.map(e => ({ stdCode: e.StdCode, toothPos: e.ToothPos || undefined, regDate: rocRegDate, source: 'grid' as const }));
            messages.push({ text: msg, severity: this.classifySeverity(msg), conflicts: d2Conflicts });
          }
        }
      } else {
        const otherStdCodes = otherEntries.map(e => e.StdCode);
        const d2NonFdiConflicts = otherEntries.filter(e =>
          disableCodes.includes(e.StdCode) && !is91013CConcurrentConflict(e.StdCode));
        if (d2NonFdiConflicts.length > 0) {
          const msg = rule.errMsg_trcode || '當日同處置不可重複申報';
          const d2c: RuleConflict[] = d2NonFdiConflicts.map(e => ({ stdCode: e.StdCode, toothPos: e.ToothPos || undefined, regDate: rocRegDate, source: 'grid' as const }));
          messages.push({ text: msg, severity: this.classifySeverity(msg), conflicts: d2c });
        }
      }
    }

    // 6. disable1 + times
    if (rule.disable1 && rule.times > 0) {
      if (rule.isfdi === 1 && !ctx.currentToothPos) {
        // 尚未填牙位，不檢核
      } else {
        const disableCodes = this.splitCodes(rule.disable1);
        const cutoffDate = (rule.period_k && rule.period_a != null)
          ? this.calcCutoffDate(regDate, rule.period_k, rule.period_a) : null;
        const isCalendarMonthDifferenceRule = this.isCalendarMonthDifferenceRule(rule);
        const isSameClaimMonthRule = this.isSameClaimMonthRule(rule);
        const regClaimMonth = isSameClaimMonthRule ? this.toClaimMonthKey(regDate) : null;

        // ── 歷史：符合條件的紀錄 + 最近日期 ──
        let latestHistDate: Date | null = null;
        const histMatches = ctx.clinicHistories.filter(h => {
          if (!disableCodes.includes(h.StdCode)) return false;
          if (isCalendarMonthDifferenceRule) {
            const hDate = this.parseRocDate(h.RegDate);
            if (!hDate || hDate > regDate) return false;
            const monthDifference = this.getCalendarMonthDifference(regDate, hDate);
            if (monthDifference < 0 || monthDifference >= rule.period_a) return false;
          } else if (isSameClaimMonthRule) {
            const hDate = this.parseRocDate(h.RegDate);
            if (!hDate || hDate > regDate || this.toClaimMonthKey(hDate) !== regClaimMonth) return false;
          } else if (cutoffDate) {
            const hDate = this.parseRocDate(h.RegDate);
            if (!hDate || hDate < cutoffDate || hDate > regDate) return false;
          }
          if (rule.isfdi === 1 && ctx.currentToothPos) {
            const cp = this.splitCodes(ctx.currentToothPos);
            const hp = h.ToothPos ? this.splitCodes(h.ToothPos) : [];
            return rule.isquadrant === 1
              ? this.hasQuadrantOverlap(cp, hp) : this.hasPositionOverlap(cp, hp);
          }
          return true;
        });
        for (const h of histMatches) {
          const hd = this.parseRocDate(h.RegDate);
          if (hd && (!latestHistDate || hd > latestHistDate)) latestHistDate = hd;
        }

        // ── 當前 grid（排除自己 & 排除同處置碼，同處置碼由 disable2 負責）──
        const otherEntries = this.getOtherGridEntries(ctx);
        const gridMatches = otherEntries.filter(e => {
          if (!disableCodes.includes(e.StdCode)) return false;
          if (e.StdCode === rule.nhitrcode) return false; // 同處置碼由 disable2 處理
          if (rule.isfdi === 1 && ctx.currentToothPos) {
            const cp = this.splitCodes(ctx.currentToothPos);
            const ep = e.ToothPos ? this.splitCodes(e.ToothPos) : [];
            return rule.isquadrant === 1
              ? this.hasQuadrantOverlap(cp, ep) : this.hasPositionOverlap(cp, ep);
          }
          return true;
        });

        if (histMatches.length + gridMatches.length >= rule.times) {
          const baseMsg = rule.errMsg_self || rule.errMsg_cycle || `在週期內已達申報上限（${rule.times}次）`;

          // ── 建構 conflicts 清單 ──
          const d1Conflicts: RuleConflict[] = [];
          for (const h of histMatches) d1Conflicts.push({ stdCode: h.StdCode, toothPos: h.ToothPos || undefined, regDate: h.RegDate, source: 'history' });
          for (const e of gridMatches) d1Conflicts.push({ stdCode: e.StdCode, toothPos: e.ToothPos || undefined, regDate: rocRegDate, source: 'grid' });
          const d1c = d1Conflicts.length > 0 ? d1Conflicts : undefined;

          // ── OD 特殊規則：每個牙位獨立產生訊息 ──
          if (baseMsg.includes('不可重複執行OD處置')) {
            const odMessages = this.classifyOD(ctx.currentToothPos, latestHistDate, regDate);
            if (odMessages.length > 0) {
              for (const om of odMessages) om.conflicts = d1c;
              messages.push(...odMessages);
            } else {
              // 牙位不在乳/恆齒範圍或已超過門檻 → 用原始訊息
              messages.push({ text: baseMsg, severity: this.classifySeverity(baseMsg), conflicts: d1c });
            }
          } else if (/已執行過拔牙|已執行過拔牙或齒切除術/.test(baseMsg)) {
            messages.push({ text: baseMsg, severity: this.classifySeverity(baseMsg), showX: true, conflicts: d1c });
          } else {
            messages.push({ text: baseMsg, severity: this.classifySeverity(baseMsg), conflicts: d1c });
          }
        }
      }
    }

    // 7. unit=1：數量鎖定為 1，同一天不可分列兩行
    if (rule.unit === 1) {
      const otherEntries = this.getOtherGridEntries(ctx);
      const duplicateEntries = otherEntries.filter(e => e.StdCode === rule.nhitrcode);
      if (duplicateEntries.length > 0) {
        const msg = `${rule.nhitrcode} 當日數量必須為 1，不可分列`;
        const unitConflicts: RuleConflict[] = duplicateEntries.map(e => ({ stdCode: e.StdCode, toothPos: e.ToothPos || undefined, source: 'grid' as const }));
        messages.push({ text: msg, severity: 'forbidden', conflicts: unitConflicts });
      }
    }

    let maxSev: RuleSeverity | null = null;
    for (const m of messages) maxSev = maxSeverityOf(maxSev, m.severity);
    return {
      passed: messages.length === 0,
      errorMessages: messages.map(m => m.text),
      messages,
      maxSeverity: maxSev,
    };
  }

  // ─── 內部工具 ───

  private getOtherGridEntries(ctx: RuleContext, currentRowIndex: number | null | undefined = ctx.currentRowIndex): NonNullable<RuleContext['existingGridEntries']> {
    const entries = ctx.existingGridEntries || [];
    if (currentRowIndex == null) return entries;

    return entries.filter((entry, idx) => {
      const entryRowIndex = entry.RowIndex;
      const rowIndex = typeof entryRowIndex === 'number' ? entryRowIndex : idx;
      return rowIndex !== currentRowIndex;
    });
  }

  private isSameClaimMonthRule(rule: DentalTreatRule): boolean {
    const msg = rule.errMsg_self || rule.errMsg_cycle || rule.errMsg_date || rule.errorMsg || '';
    return (rule.period_k || '').toUpperCase() === 'M' && rule.period_a === 1 && msg.includes('同月');
  }

  /** Rules whose frequency window is measured by the difference between claim months. */
  private isCalendarMonthDifferenceRule(rule: DentalTreatRule): boolean {
    const code = (rule.nhitrcode || '').trim().toUpperCase();
    return DentalTreatRuleService.CALENDAR_MONTH_DIFFERENCE_CODES.has(code)
      && (rule.period_k || '').trim().toUpperCase() === 'M'
      && rule.period_a > 0;
  }

  private getCalendarMonthDifference(currentDate: Date, historyDate: Date): number {
    return (currentDate.getFullYear() - historyDate.getFullYear()) * 12
      + currentDate.getMonth() - historyDate.getMonth();
  }

  private toClaimMonthKey(date: Date | null): string | null {
    if (!date || isNaN(date.getTime())) return null;
    return `${date.getFullYear()}-${date.getMonth() + 1}`;
  }

  /**
   * 收集 ABparent 規則所需的歷史 / grid 紀錄與牙位集合：
   *  - parentHistories: 符合 period 的歷史 ABparent 紀錄
   *  - parentGridEntries: 當次 grid 中其他 ABparent 行
   *  - histPositions / gridPositions: 對應的牙位集合（大寫）
   *  - validPositions: histPositions ∪ gridPositions
   *  - startDateStr: 最早 ABparent 看診日的民國年字串（'yyy/MM/dd'）
   */
  collectABParentInfo(
    rule: DentalTreatRule,
    ctx: RuleContext,
    regDate: Date
  ): {
    parentHistories: { StdCode: string; RegDate: string; ToothPos: string; DxCodes?: string[] }[];
    parentGridEntries: { StdCode: string; ToothPos: string; Id?: number; RowIndex?: number }[];
    histPositions: Set<string>;
    gridPositions: Set<string>;
    validPositions: Set<string>;
    startDateStr: string;
  } {
    const abCutoff = (rule.period_k && rule.period_a != null)
      ? this.calcCutoffDate(regDate, rule.period_k, rule.period_a) : null;
    let parentHistories = ctx.clinicHistories.filter(h => {
      if (h.StdCode !== rule.ABparent) return false;
      if (abCutoff) {
        const hDate = this.parseRocDate(h.RegDate);
        if (!hDate || hDate < abCutoff || hDate > regDate) return false;
      }
      return true;
    });
    const otherGridEntries = this.getOtherGridEntries(ctx);
    let parentGridEntries = otherGridEntries.filter(e => e.StdCode === rule.ABparent);
    const sameTreatmentToothScope = this.getSameTreatmentToothScope(ctx);
    if (sameTreatmentToothScope.length > 0) {
      parentHistories = parentHistories.filter(h => this.hasToothScopeOverlap(h.ToothPos, sameTreatmentToothScope));
      parentGridEntries = parentGridEntries.filter(e => this.hasToothScopeOverlap(e.ToothPos, sameTreatmentToothScope));
    }

    let histPositions = new Set<string>();
    let gridPositions = new Set<string>();
    for (const h of parentHistories) {
      if (h.ToothPos) this.splitCodes(h.ToothPos).forEach(p => histPositions.add(p.toUpperCase()));
    }
    for (const e of parentGridEntries) {
      if (e.ToothPos) this.splitCodes(e.ToothPos).forEach(p => gridPositions.add(p.toUpperCase()));
    }
    if (sameTreatmentToothScope.length > 0) {
      histPositions = this.restrictPositionsToScope(histPositions, sameTreatmentToothScope);
      gridPositions = this.restrictPositionsToScope(gridPositions, sameTreatmentToothScope);
    }
    const validPositions = new Set<string>();
    histPositions.forEach(p => validPositions.add(p));
    gridPositions.forEach(p => validPositions.add(p));

    // 取最早的療程開始日
    let earliest: Date | null = null;
    for (const h of parentHistories) {
      const hd = this.parseRocDate(h.RegDate);
      if (hd && (!earliest || hd < earliest)) earliest = hd;
    }
    const startDateStr = earliest
      ? `${earliest.getFullYear() - 1911}/${String(earliest.getMonth() + 1).padStart(2, '0')}/${String(earliest.getDate()).padStart(2, '0')}`
      : '';

    return { parentHistories, parentGridEntries, histPositions, gridPositions, validPositions, startDateStr };
  }

  /** Limit AB rule context to the current SameTreat 5/6 course tooth scope. */
  private getSameTreatmentToothScope(ctx: RuleContext): string[] {
    const sameTreat = (ctx.sameTreat || '').toString().trim();
    if (ctx.registerClass !== 'AB' || (sameTreat !== '5' && sameTreat !== '6')) return [];
    return this.normalizeToothScope(ctx.sameTreatmentToothPositions || []);
  }

  private normalizeToothScope(value: string[] | string): string[] {
    const items = Array.isArray(value) ? value : this.splitCodes(value || '');
    const result: string[] = [];
    for (const item of items) {
      const token = (item || '').toString().trim().toUpperCase();
      if (!token) continue;
      this.splitCodes(token).forEach(p => {
        const pos = (p || '').toString().trim().toUpperCase();
        if (/^\d+$/.test(pos) && pos.length > 2 && pos.length % 2 === 0) {
          for (let i = 0; i < pos.length; i += 2) {
            const part = pos.substring(i, i + 2);
            if (part && !result.includes(part)) result.push(part);
          }
          return;
        }
        if (pos && !result.includes(pos)) result.push(pos);
      });
    }
    return result;
  }

  private hasToothScopeOverlap(toothPos: string, scope: string[]): boolean {
    if (!scope.length) return true;
    if (!toothPos) return false;
    return scope.some(scopePos => this.toothOverlap(toothPos, scopePos));
  }

  private restrictPositionsToScope(positions: Set<string>, scope: string[]): Set<string> {
    if (!scope.length) return positions;
    const result = new Set<string>();
    const rawPositions = Array.from(positions);
    for (const scopePos of scope) {
      if (rawPositions.some(pos => this.toothOverlap(pos, scopePos))) {
        result.add(scopePos);
      }
    }
    return result;
  }

  /** 將 validPositions Set 排序輸出為以逗號連接的字串 */
  formatValidPositions(positions: Set<string>): string {
    return Array.from(positions).sort().join(',');
  }

  /**
   * 對外公開：取得 checkAB 規則的 AB 療程上下文。
   * 用於 order-grid-dental 在使用者選入處置時自動帶入牙位/PCS、
   * 以及 record-dental 在 AB 療程下直接帶入歷史診斷碼跳過 helper。
   *
   * @param nhitrcode 處置代碼
   * @param ctx 當前 ruleContext（不需 currentToothPos）
   * @returns 若無 checkAB 規則 / 找不到 ABparent 紀錄，回傳 null
   *          否則回傳合理牙位、母處置紀錄、診斷碼聯集等資訊
   */
  getABTherapyContext(
    nhitrcode: string,
    ctx: Omit<RuleContext, 'currentToothPos'>
  ): {
    validPositions: string[];
    parentHistories: { StdCode: string; RegDate: string; ToothPos: string; DxCodes?: string[] }[];
    parentDxCodes: string[];
    startDateStr: string;
    /** 觸發的當日有效 checkAB 規則（取第一筆） */
    rule: DentalTreatRule;
  } | null {
    const rules = this.getRulesByNhitrcode(nhitrcode);
    const regDate = new Date(ctx.regDate);
    const checkABRule = rules.find(r =>
      r.checkAB === 1
      && !!r.ABparent
      && this.checkDateRange(r, regDate));
    if (!checkABRule) return null;

    const dummyCtx: RuleContext = { ...ctx } as RuleContext;
    const abInfo = this.collectABParentInfo(checkABRule, dummyCtx, regDate);
    if (abInfo.validPositions.size === 0) return null;

    // 若規則有限制 fdi，則合理牙位需與 fdi 交集
    let validList: string[];
    if (checkABRule.fdi) {
      const fdiSet = new Set(this.splitCodes(checkABRule.fdi).map(p => p.toUpperCase()));
      validList = Array.from(abInfo.validPositions).filter(p => this.hasABPositionOverlap([p], fdiSet));
    } else {
      validList = Array.from(abInfo.validPositions);
    }
    validList.sort();

    // 收集所有母處置歷史的診斷碼聯集（去重）
    // 支援多種歷史資料格式：優先使用 DxCodes 陣列，若無則備援檢查 Dx1..Dx6 等欄位
    const dxSet = new Set<string>();
    for (const h of abInfo.parentHistories) {
      // 1) 若 API 回傳 DxCodes 陣列（預期格式）
      if (Array.isArray((h as any).DxCodes) && (h as any).DxCodes.length > 0) {
        ((h as any).DxCodes as string[]).forEach(d => { if (d) dxSet.add(d); });
        continue;
      }
      // 2) 備援：檢查可能存在的 Dx1..Dx6 欄位（舊格式或其他來源）
      for (let n = 1; n <= 6; n++) {
        const key = 'Dx' + n;
        const val = (h as any)[key];
        if (val && typeof val === 'string' && val.trim().length > 0) dxSet.add(val.trim());
      }
      // 3) 備援：有些 DTO 可能使用不同命名（DxCodes 以外），嘗試常見欄位
      const alt = (h as any).Dx || (h as any).Diagnosis || (h as any).DxCode || null;
      if (alt && typeof alt === 'string' && alt.trim()) dxSet.add(alt.trim());
    }
    const parentDxCodes = Array.from(dxSet);

    return {
      validPositions: validList,
      parentHistories: abInfo.parentHistories,
      parentDxCodes,
      startDateStr: abInfo.startDateStr,
      rule: checkABRule,
    };
  }

  /** 檢查此規則在看診日是否有效（有效才需要檢核） */
  private checkDateRange(rule: DentalTreatRule, regDate: Date): boolean {
    const start = this.parseDate(rule.start_date);
    const end = this.parseDate(rule.end_date);
    if (start && regDate < start) return false;
    if (end && regDate > end) return false;
    return true;
  }

  /** 公開版本：供外部呼叫檢查規則日期有效性 */
  checkDateRangePublic(rule: DentalTreatRule, regDate: Date): boolean {
    return this.checkDateRange(rule, regDate);
  }

  /** 解析日期字串，無效 / NULL 回傳 null */
  private parseDate(s: string | null): Date | null {
    if (!s || s.trim() === '') return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * 解析民國年日期字串 (yyy/MM/dd 或 yyy-MM-dd) 為 Date。
   * 例如 '115/03/13' → 2026-03-13。
   * 若已是西元格式或無法解析，回退為 new Date(s)。
   */
  parseRocDate(s: string): Date | null {
    if (!s || s.trim() === '') return null;
    // 嘗試民國年格式: yyy/MM/dd 或 yyy-MM-dd
    const m = s.match(/^(\d{2,3})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
    if (m) {
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      const day = parseInt(m[3], 10);
      // 民國年不超過 200，若超過則可能是西元年
      if (year <= 200) {
        const d = new Date(year + 1911, month - 1, day);
        return isNaN(d.getTime()) ? null : d;
      }
    }
    // 回退：嘗試標準日期解析
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  /** 以半形逗號 split，去除空白與尾碼逗號 */
  private splitCodes(s: string): string[] {
    return s.split(',').map(c => c.trim()).filter(c => c.length > 0);
  }

  /**
   * 根據 period_k / period_a 計算截止日（往前推）
   * M: 月份計算不精算到日（年*12+月）
   * Y: 年
   * D: 日
   */
  private calcCutoffDate(regDate: Date, periodK: string, periodA: number): Date | null {
    const k = periodK.toUpperCase();
    if (k === 'D') {
      const d = new Date(regDate);
      d.setDate(d.getDate() - periodA);
      return d;
    }
    if (k === 'M') {
      // 月份計算：只算到月（參照 isInfantDecay 邏輯）
      const d = new Date(regDate);
      d.setMonth(d.getMonth() - periodA);
      d.setDate(1); // 回到月初，不精算到日
      return d;
    }
    if (k === 'Y') {
      const d = new Date(regDate);
      d.setFullYear(d.getFullYear() - periodA);
      return d;
    }
    // period_k = '1' 或其他先不處理
    return null;
  }

  /**
   * 將齒號轉換成象限代碼
   * 11~19, 51~55 → UR；21~29, 61~65 → UL；31~39, 71~75 → LL；41~49, 81~85 → LR
   * 若本身就是象限碼（UR/UL/LL/LR）直接回傳；其他（FM, 99 等）回傳 null
   */
  toQuadrant(pos: string): string | null {
    const upper = pos.toUpperCase();
    if (['UR', 'UL', 'LL', 'LR'].includes(upper)) return upper;
    const num = parseInt(pos, 10);
    if (isNaN(num)) return null;
    const tens = Math.floor(num / 10);
    // 恆齒: 1x=UR, 2x=UL, 3x=LL, 4x=LR；乳齒: 5x=UR, 6x=UL, 7x=LL, 8x=LR
    switch (tens) {
      case 1: case 5: return 'UR';
      case 2: case 6: return 'UL';
      case 3: case 7: return 'LL';
      case 4: case 8: return 'LR';
      default: return null;
    }
  }

  /** 兩組牙位是否有交集 */
  private hasPositionOverlap(posA: string[], posB: string[]): boolean {
    return posA.some(a => posB.some(b => a.toUpperCase() === b.toUpperCase()));
  }

  /** 兩組牙位換算象限後是否有交集 */
  private hasQuadrantOverlap(posA: string[], posB: string[]): boolean {
    const qA = posA.map(p => this.toQuadrant(p)).filter(q => q !== null) as string[];
    const qB = posB.map(p => this.toQuadrant(p)).filter(q => q !== null) as string[];
    return qA.some(a => qB.includes(a));
  }

  /** ABparent 部位可用較寬鬆的牙位重疊判斷，FM/99 視為全口。 */
  private hasABPositionOverlap(posA: Iterable<string>, posB: Iterable<string>): boolean {
    const a = Array.from(posA).map(p => (p || '').toString().trim().toUpperCase()).filter(p => p.length > 0);
    const b = Array.from(posB).map(p => (p || '').toString().trim().toUpperCase()).filter(p => p.length > 0);
    return a.some(pa => b.some(pb => pa === pb || this.toothOverlap(pa, pb)));
  }

  /** 將牙位字串（齒號/象限/部位，逗號分隔）展開為個別齒號集合 */
  expandToTeeth(pos: string): Set<string> {
    const result = new Set<string>();
    if (!pos) return result;
    const parts = pos.split(',').map(p => p.trim().toUpperCase());
    for (const p of parts) {
      if (p === 'FM' || p === '99') {
        FULL_MOUTH_TEETH.forEach(t => result.add(t));
      } else if (REGION_TO_TEETH[p]) {
        REGION_TO_TEETH[p].forEach(t => result.add(t));
      } else if (/^\d+$/.test(p)) {
        result.add(p);
      }
    }
    return result;
  }

  /** 檢查兩個牙位是否有重疊（支援齒號、象限、部位、逗號分隔） */
  toothOverlap(posA: string, posB: string): boolean {
    const setA = this.expandToTeeth(posA);
    const setB = this.expandToTeeth(posB);
    return Array.from(setA).some(t => setB.has(t));
  }

  // ─── 牙位 helper 用 API ───

  /**
   * 針對指定處置碼，檢查每個候選牙位是否違反 isfdi/isquadrant 相關規則。
   * 回傳 Map<牙位, RuleMessage[]>，只有不通過的牙位才會出現在 Map 中。
   */
  getDisabledToothPositions(
    nhitrcode: string,
    ctx: Omit<RuleContext, 'currentToothPos'>,
    currentRowIndex?: number
  ): Map<string, RuleMessage[]> {
    const result = new Map<string, RuleMessage[]>();
    const rules = this.getRulesByNhitrcode(nhitrcode);

    // 收集所有齒號（含恆齒/乳齒/象限）
    const allPositions = [
      'FM', '99',
      'UR', 'UA', 'UL', 'UB', 'LR', 'LA', 'LL', 'LB',
      ...Array.from({ length: 9 }, (_, i) => (11 + i).toString()),
      ...Array.from({ length: 9 }, (_, i) => (21 + i).toString()),
      ...Array.from({ length: 9 }, (_, i) => (31 + i).toString()),
      ...Array.from({ length: 9 }, (_, i) => (41 + i).toString()),
      ...Array.from({ length: 5 }, (_, i) => (51 + i).toString()),
      ...Array.from({ length: 5 }, (_, i) => (61 + i).toString()),
      ...Array.from({ length: 5 }, (_, i) => (71 + i).toString()),
      ...Array.from({ length: 5 }, (_, i) => (81 + i).toString()),
    ];
    for (const pos of allPositions) {
      const unavailableToothMessage = this.getDentalChartUnavailableToothMessage(
        { ...ctx, currentToothPos: pos },
        pos);
      if (unavailableToothMessage) result.set(pos, [unavailableToothMessage]);
    }
    if (rules.length === 0) return result;

    // 分離規則
    const fdiRulesNonAB = rules.filter(r => r.isfdi === 1 && !(r.checkAB === 1 && r.ABparent));
    const checkABFdiRules = rules.filter(r => r.isfdi === 1 && r.checkAB === 1 && !!r.ABparent);
    if (fdiRulesNonAB.length === 0 && checkABFdiRules.length === 0) return result;

    // ── 1. 非 checkAB 規則：逐牙位 validateRule──
    if (fdiRulesNonAB.length > 0) {
      for (const pos of allPositions) {
        const posCtx: RuleContext = { ...ctx, currentToothPos: pos, currentRowIndex };
        const msgs: RuleMessage[] = [];
        for (const rule of fdiRulesNonAB) {
          const r = this.validateRule(rule, posCtx);
          if (!r.passed) {
            msgs.push(...r.messages);
          }
        }
        if (msgs.length > 0) {
          result.set(pos, [...(result.get(pos) || []), ...msgs]);
        }
      }
    }

    // ── 2. checkAB+isfdi=1：反向標記（含 period_k + period_a 日期過濾） ──
    // 三種情境：
    //   A. registerClass === 'AB'：fdiSet 但不在合理牙位內 → forbidden（新訊息）
    //   B. 非AB：
    //      - pos 在 ABparent grid 內 → 合規，不標
    //      - pos 在 ABparent 歷史內 → forbidden（"該牙位有療程開始日..."）
    //      - pos 不在任何 ABparent 內 → info（"{pos}尚未開始療程，請確認已執行 {ABparent}！"）
    if (checkABFdiRules.length > 0) {
      const regDate = new Date(ctx.regDate);
      const isAB = ctx.registerClass === 'AB';

      for (const rule of checkABFdiRules) {
        if (!this.checkDateRange(rule, regDate)) continue;

        // 借用 validateRule 的同款收集邏輯
        const dummyCtx: RuleContext = { ...ctx, currentRowIndex } as RuleContext;
        const abInfo = this.collectABParentInfo(rule, dummyCtx, regDate);

        // 無任何 ABparent 紀錄 → 不需特別標記（非AB也無 "尚未開始療程" 資料來源）
        if (abInfo.validPositions.size === 0) continue;

        const fdiSet = rule.fdi
          ? new Set(this.splitCodes(rule.fdi).map(p => p.toUpperCase()))
          : null;
        const validList = this.formatValidPositions(abInfo.validPositions);

        for (const pos of allPositions) {
          const posUpper = pos.toUpperCase();
          if (fdiSet && !this.hasABPositionOverlap([posUpper], fdiSet)) continue;

          const inGrid = this.hasABPositionOverlap([posUpper], abInfo.gridPositions);
          const inHist = this.hasABPositionOverlap([posUpper], abInfo.histPositions);
          const inValid = inGrid || inHist;

          let msg: string | null = null;
          let severity: RuleSeverity | null = null;
          let conflicts: RuleConflict[] | undefined;

          if (isAB) {
            // AB 療程：合理牙位以外 → forbidden（新訊息）
            if (!inValid) {
              msg = `本次療程開始日${abInfo.startDateStr}僅能處理${validList}，如需處理其他部位請另掛一筆或變更本次掛號療程`;
              severity = 'forbidden';
              conflicts = [
                ...abInfo.parentHistories.map(h => ({ stdCode: h.StdCode, toothPos: h.ToothPos || undefined, regDate: h.RegDate, source: 'history' as const })),
                ...abInfo.parentGridEntries.map(e => ({ stdCode: e.StdCode, toothPos: e.ToothPos || undefined, source: 'grid' as const })),
              ];
            }
          } else {
            // 非AB
            if (inGrid) {
              // ABparent 在當次 grid → 合規，不標
              continue;
            }
            if (inHist) {
              // ABparent 僅在歷史 → forbidden（要求改掛AB）
              msg = rule.errorMsg || '該牙位有療程開始日，請改掛AB療程！';
              severity = 'forbidden';
              conflicts = abInfo.parentHistories
                .filter(h => h.ToothPos && this.hasABPositionOverlap([posUpper], this.splitCodes(h.ToothPos)))
                .map(h => ({ stdCode: h.StdCode, toothPos: h.ToothPos || undefined, regDate: h.RegDate, source: 'history' as const }));
            } else {
              // 91013C 也可搭配牙體復形，未命中該牙位療程時由主處置覆蓋檢核負責。
              if (nhitrcode === TREAT_91013C) continue;
              // pos 沒有 ABparent 紀錄
              msg = pos + ' 尚未開始療程，請確認已執行 ' + rule.ABparent + '！';
              severity = 'info';
              // 不用再特別提升 severity 來區分 5/6 同療，因為現在會根據處置自動切換 registerClass，使用者不再需要手動切換療 5/6
            }
          }

          if (msg && severity) {
            const existing = result.get(pos) || [];
            existing.push({ text: msg, severity, conflicts: conflicts && conflicts.length > 0 ? conflicts : undefined });
            result.set(pos, existing);
          }
        }
      }
    }

    // ── 3. isanes/isxray 反向標記（96001C / 34xxx 被其他 isanes/isxray 處置禁用的牙位） ──
    const isTarget96001 = nhitrcode === '96001C';
    const isTarget34xxx = nhitrcode.startsWith('34');
    if (isTarget96001 || isTarget34xxx) {
      const reverseRegDate = new Date(ctx.regDate);
      const otherEntries = this.getOtherGridEntries(ctx, currentRowIndex);
      for (const entry of otherEntries) {
        if (!entry.StdCode || !entry.ToothPos) continue;
        if (isTarget96001 && entry.StdCode === '96001C') continue;
        if (isTarget34xxx && entry.StdCode === nhitrcode) continue;
        const entryRules = this.getRulesByNhitrcode(entry.StdCode);
        for (const rule of entryRules) {
          const matchAnes = isTarget96001 && rule.isanes === 1;
          const matchXray = isTarget34xxx && rule.isxray === 1;
          if (!matchAnes && !matchXray) continue;
          if (!this.checkDateRange(rule, reverseRegDate)) continue;
          const triggerTeeth = this.expandToTeeth(entry.ToothPos);
          if (triggerTeeth.size === 0) continue;
          let msg = matchAnes
            ? (rule.errMsg_anes || `${entry.StdCode}已內含麻醉96001C費用`)
            : (rule.errMsg_xray || `${entry.StdCode}已內含X光費用`);
          msg = msg.replace(/該處置/g, entry.StdCode);
          const triggerTeethArr = Array.from(triggerTeeth);
          for (const pos of allPositions) {
            const posTeeth = this.expandToTeeth(pos);
            if (!triggerTeethArr.some(t => posTeeth.has(t))) continue;
            const existing = result.get(pos) || [];
            existing.push({
              text: msg,
              severity: 'forbidden' as RuleSeverity,
              conflicts: [{ stdCode: entry.StdCode, toothPos: entry.ToothPos, source: 'grid' as const }],
            });
            result.set(pos, existing);
          }
        }
      }
    }

    result.forEach((messages, position) => {
      result.set(position, this.adjustExternalHistorySeverities(messages, ctx));
    });
    return result;
  }

  /** 從規則中收集所有非 null 的 errMsg_* 欄位 */
  collectErrorMessages(rule: DentalTreatRule): string[] {
    const msgs: string[] = [];
    const fields: (keyof DentalTreatRule)[] = [
      'errorMsg', 'errMsg_Fdi', 'errMsg_cycle', 'errMsg_age', 'errMsg_status',
      'errMsg_xray', 'errMsg_anes', 'errMsg_indep', 'errMsg_indep2',
      'errMsg_trcode', 'errMsg_self', 'errMsg_date', 'errMsg_surface', 'errMsg_icd10'
    ];
    for (const f of fields) {
      const v = rule[f];
      if (v && typeof v === 'string') msgs.push(v);
    }
    return msgs;
  }
}
